import { type FieldValues, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { mergeDeepWith } from 'ramda';
import {
  calcPayoutRate,
  calcChargeHours,
  calcPayHours,
} from 'resources/rate_settings/rateSettingsCalculations';
import _, { isNil } from 'lodash';
import type { ClassificationSpecificInheritedSettings } from '../types/inheritedSettings.types';

type OvertimeConfigurationValues = {
  charge_double_overtime_multiplier: number;
  charge_max_daily_regular_hours: number;
  charge_max_daily_overtime_hours: number;
  charge_overtime_multiplier: number;
  payout_max_daily_regular_hours: number;
  payout_max_daily_overtime_hours: number;
  payout_overtime_multiplier: number;
  payout_double_overtime_multiplier: number;
  blended_rate_markup?: number;
};

type UpdateProRateCentsProps = {
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  source: string;
  baseMarginPercent: number;
  rateCents: number;
  inheritedSettings: ClassificationSpecificInheritedSettings;
  overtimeConfigurationValues?: OvertimeConfigurationValues;
};

type UpdateChargeRateCentsProps = {
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  source: string;
  professionalRateCents: number;
  baseMarginPercent: number;
  inheritedSettings: ClassificationSpecificInheritedSettings;
  blendedMarkup: number;
  overtimeConfigurationValues?: OvertimeConfigurationValues;
};

type GetInheritedSettingsProps = {
  source: string;
  watch: UseFormWatch<FieldValues>;
  inheritedSettings: ClassificationSpecificInheritedSettings;
};

type CalculateNewTotalMarginPercentW2Props = {
  watch: UseFormWatch<FieldValues>;
  source: string;
  baseW2Margin: number;
  setValue: UseFormSetValue<FieldValues>;
};

type CalculateNewBaseMarginPercentW2Props = {
  watch: UseFormWatch<FieldValues>;
  source: string;
  totalMarginPercentW2: number;
  setValue: UseFormSetValue<FieldValues>;
};

export const getInheritedSettings = ({
  source,
  inheritedSettings,
  watch,
}: GetInheritedSettingsProps) => {
  const formData = watch(source);
  const { tab } = getClassificationFromSource(source);

  if (inheritedSettings?.ten99 || inheritedSettings?.w2) {
    inheritedSettings = _.get(inheritedSettings, tab);
  }

  return mergeDeepWith((a, b) => a ?? b, formData || {}, inheritedSettings || {});
};

export const getSourceAndLabel = (source: string, useBaseMargin: boolean = false) => {
  const typeMapping = {
    'w2.local': useBaseMargin
      ? {
          source: `${source}.base_margin_percent_w2`,
          label: 'Base Margin Percent W2',
          field: 'base_margin_percent_w2',
        }
      : {
          source: `${source}.total_margin_percent_w2`,
          label: 'Total Margin Percent W2',
          field: 'total_margin_percent_w2',
        },
    'w2.travel': {
      source: `${source}.margin_percent_w2_travel`,
      label: 'Margin Percent W2 Travel',
      field: 'margin_percent_w2_travel',
    },
    ten99: {
      source: `${source}.margin_percent_1099`,
      label: 'Margin Percent 1099',
      field: 'margin_percent_1099',
    },
  };

  const matchedType = Object.keys(typeMapping).find((type) => source.includes(type));
  return matchedType ? typeMapping[matchedType] : { source: '', label: '' };
};

// verify if w2 travel rate configuration exists. And if not, use w2 local rate configuration
const getSettingsWithFallback = (
  mergedSettings: any,
  w2LocalMergedSettings: any,
  source: string,
) => {
  const { use_blended_rate, blended_rates, non_blended_rates } = mergedSettings || {};
  let settingsToUse = use_blended_rate ? blended_rates : non_blended_rates;

  if (
    (!settingsToUse?.charge_double_overtime_multiplier ||
      !settingsToUse?.charge_max_daily_regular_hours ||
      !settingsToUse?.charge_max_daily_overtime_hours ||
      !settingsToUse?.charge_overtime_multiplier ||
      !settingsToUse?.payout_max_daily_regular_hours ||
      !settingsToUse?.payout_max_daily_overtime_hours ||
      !settingsToUse?.payout_overtime_multiplier ||
      !settingsToUse?.payout_double_overtime_multiplier) &&
    source.includes('w2')
  ) {
    const {
      use_blended_rate: w2UseBlendedRate,
      blended_rates: w2BlendedRates,
      non_blended_rates: w2NonBlendedRates,
    } = w2LocalMergedSettings || {};
    settingsToUse = w2UseBlendedRate ? w2BlendedRates : w2NonBlendedRates;
  }

  return settingsToUse || {};
};

export const updateProRateCents = ({
  setValue,
  watch,
  source,
  rateCents,
  baseMarginPercent,
  inheritedSettings,
  overtimeConfigurationValues,
}: UpdateProRateCentsProps) => {
  const { level } = getClassificationFromSource(source);

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const w2LocalMergedSettings = getInheritedSettings({
    source: `${level}.w2.local`,
    inheritedSettings,
    watch,
  });

  const settingsToUse =
    overtimeConfigurationValues ||
    getSettingsWithFallback(mergedSettings, w2LocalMergedSettings, source);

  const {
    charge_double_overtime_multiplier,
    charge_max_daily_regular_hours,
    charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  } = settingsToUse;

  let totalMargin = baseMarginPercent;

  if (source.includes('w2.local')) {
    totalMargin = calculateNewTotalMarginPercentW2({
      baseW2Margin: baseMarginPercent,
      setValue,
      source,
      watch,
    });
  }

  const { payoutRate } = calcPayoutRate({
    hours: 12,
    chargeRate: rateCents,
    charge_double_overtime_multiplier,
    margin_percent: totalMargin,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  });

  setValue(`${source}.pro_rate_cents`, payoutRate + 10);
};

export const updateChargeRateCents = ({
  setValue,
  watch,
  source,
  professionalRateCents,
  baseMarginPercent,
  inheritedSettings,
  overtimeConfigurationValues,
  blendedMarkup,
}: UpdateChargeRateCentsProps) => {
  const { level } = getClassificationFromSource(source);

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const w2LocalMergedSettings = getInheritedSettings({
    source: `${level}.w2.local`,
    inheritedSettings,
    watch,
  });

  const isUsingBlendedRates = mergedSettings?.use_blended_rate;

  const settingsToUse =
    overtimeConfigurationValues ||
    getSettingsWithFallback(mergedSettings, w2LocalMergedSettings, source);

  const {
    charge_double_overtime_multiplier,
    charge_max_daily_regular_hours,
    charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  } = settingsToUse;

  const hours = 12;

  const chargeHours = calcChargeHours({
    hours,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  });

  if (payHours === 0) {
    return;
  }

  let totalMargin = baseMarginPercent;

  if (source.includes('w2.local')) {
    totalMargin = calculateNewTotalMarginPercentW2({
      baseW2Margin: baseMarginPercent,
      setValue,
      source,
      watch,
    });
  }
  const normalizedMargin = Number(totalMargin || 0);
  if (normalizedMargin >= 1) {
    return;
  }

  const appliedChargeRate = Math.round(
    (professionalRateCents * payHours) / (chargeHours * (1 - normalizedMargin)),
  );
  const chargeRateWithoutMarkup = Math.round(appliedChargeRate / (1 + blendedMarkup));
  const chargeRateWithMarkup = Math.round(appliedChargeRate * (1 + blendedMarkup));

  if (isUsingBlendedRates) {
    setValue(`${source}.blended_rate_cents`, appliedChargeRate);
    setValue(`${source}.charge_rate_cents`, chargeRateWithoutMarkup);
  } else {
    setValue(`${source}.charge_rate_cents`, appliedChargeRate);
    !isNil(blendedMarkup) && setValue(`${source}.blended_rate_cents`, chargeRateWithMarkup);
  }
};

export const getClassificationFromSource = (source: string) => {
  const [level, hash, subhash] = source.split('.');
  const tab = `${hash}${subhash ? `.${subhash}` : ''}`;

  return {
    level,
    tab,
  };
};

export const calculateNewTotalMarginPercentW2 = ({
  watch,
  baseW2Margin,
  source,
  setValue,
}: CalculateNewTotalMarginPercentW2Props) => {
  const w2InheritedSettings = watch('settings.w2') || {};

  const {
    social_security_burden_multiplier,
    medicare_burden_multiplier,
    federal_unemployment_percent_burden_multiplier,
    state_unemployment_percent_burden_multiplier,
    operational_burden_percent,
  } = w2InheritedSettings;

  const burdenMultipliersSum =
    social_security_burden_multiplier +
    medicare_burden_multiplier +
    federal_unemployment_percent_burden_multiplier +
    state_unemployment_percent_burden_multiplier +
    operational_burden_percent;

  const totalMarginPercentW2 =
    (Number(baseW2Margin) + burdenMultipliersSum) / (1 + burdenMultipliersSum);

  if (totalMarginPercentW2 && !isNaN(Number(totalMarginPercentW2))) {
    setValue(`${source}.total_margin_percent_w2`, totalMarginPercentW2.toFixed(5));
  }

  return totalMarginPercentW2;
};

export const calculateNewBaseMarginPercentW2 = ({
  watch,
  totalMarginPercentW2,
  source,
  setValue,
}: CalculateNewBaseMarginPercentW2Props) => {
  const w2InheritedSettings = watch('settings.w2') || {};

  const {
    social_security_burden_multiplier,
    medicare_burden_multiplier,
    federal_unemployment_percent_burden_multiplier,
    state_unemployment_percent_burden_multiplier,
    operational_burden_percent,
  } = w2InheritedSettings;

  const burdenMultipliersSum =
    social_security_burden_multiplier +
    medicare_burden_multiplier +
    federal_unemployment_percent_burden_multiplier +
    state_unemployment_percent_burden_multiplier +
    operational_burden_percent;

  const baseW2Margin =
    Number(totalMarginPercentW2) * (1 + burdenMultipliersSum) - burdenMultipliersSum;

  if (baseW2Margin && !isNaN(Number(baseW2Margin))) {
    setValue(`${source}.base_margin_percent_w2`, baseW2Margin.toFixed(5));
  }

  return baseW2Margin;
};
